import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    Grid,
    Typography,
    Button,
    Box,
    Card,
    CardContent,
    CardHeader,
    CardActions,
    Paper,
    CircularProgress,
    useMediaQuery,
    useTheme,
    Divider
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useHistory } from 'react-router-dom';
import { useDeadline } from '../../contexts/DeadlineContext';
const RequiredForEvent = () => {
    const history = useHistory();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { abstractHomeSetup } = useDeadline();
    const gradientBackground = '#05597D';// Example gradient background
    const [homeSetup, setHomeSetup] = useState({});
    const [settings, setSettings] = useState({});
    const [getOptionItems, setGetOptionItems] = useState({});
    const [conferenceEmails, setConferenceEmails] = useState([]);
    const [workshopEmails, setWorkshopEmails] = useState([]);
    const [exhibitionEmails, setExhibitionEmails] = useState([]);
    const [abstractEmails, setAbstractEmails] = useState([]);
    const [loading, setLoading] = useState(true);

    const eventid = localStorage.getItem('eventid');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [homeRes, settingsRes, optionsRes, emailTemplatesRes, workshopEmailTemRes, exhibitionEmailTempRes, abstractEmailTemRes] = await Promise.all([
                    axios.get(`/api/add-home-setup-index?id=${eventid}`),
                    axios.get(`/api/get-setting/${eventid}`),
                    axios.get(`api/index-option-setup?id=${eventid}`),
                    axios.get(`/api/index-conference-registration-email-template?id=${eventid}`),
                    axios.get(`/api/index-workshop-registration-email-template?id=${eventid}`),
                    axios.get(`/api/index-exhibition-registration-email-template?id=${eventid}`),
                    axios.get(`/api/index-abstract-registration-email-template?id=${eventid}`),
                ]);

                if (homeRes.status === 200) {
                    setHomeSetup(homeRes.data.HomePageSetup || {});
                }

                if (settingsRes.status === 200) {
                    setSettings(settingsRes.data.settings || {});
                }

                if (optionsRes.status === 200) {
                    setGetOptionItems(optionsRes.data.getOptions || {});
                }
                if (emailTemplatesRes.status === 200) {
                    setConferenceEmails(emailTemplatesRes.data.WorkshopEmailTemp || []);
                }
                if (workshopEmailTemRes.status === 200) {
                    setWorkshopEmails(workshopEmailTemRes.data.WorkshopEmailTemp || []);
                }
                if (exhibitionEmailTempRes.status === 200) {
                    setExhibitionEmails(exhibitionEmailTempRes.data.ExhibitionEmailTemp || []);
                }
                if (abstractEmailTemRes.status === 200) {
                    setAbstractEmails(abstractEmailTemRes.data.AbstractEmailTemp || []);
                }
            } catch (error) {
                console.error("Error fetching data", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [eventid]);

    const renderIcon = (value) =>
        value ? <CheckIcon style={{ color: 'green' }} /> : <CloseIcon style={{ color: 'red' }} />;

    const renderIconForImage = (url) =>
        url && !url.includes('no_image.jpg')
            ? <CheckIcon style={{ color: 'green' }} />
            : <CloseIcon style={{ color: 'red' }} />;

    const isSMTPConfigured = () => {
        return (
            settings.MAIL_HOST &&
            settings.MAIL_USERNAME &&
            settings.MAIL_PASSWORD &&
            settings.MAIL_PORT &&
            settings.MAIL_PROTOCOL &&
            settings.MAIL_FROM &&
            settings.ENABLED_MAIL === "1"
        );
    };

    const hasTemplate = (titleId) => {
        return conferenceEmails.some(template => template.title_id === titleId && template.status === 1);
    };
    const hasWorkshopTemplate = (titleId) => {
        return workshopEmails.some(template => template.title_id === titleId && template.status === 1);
    };
    const hasExhibitionTemplate = (titleId) => {
        return exhibitionEmails.some(template => template.title_id === titleId && template.status === 1);
    };
    const hasAbstractTemplate = (titleId) => {
        return abstractEmails.some(template => template.title_id === titleId && template.status === 1);
    };

    const sections = [
        {
            title: "Banner Status For Home Page",
            buttonText: "Banner Setup",
            path: "/admin/abstract/home-page",
            items: [
                { label: 'Event Logo', value: homeSetup.eventLogo, render: renderIcon },
                { label: 'Abstract Banner', value: homeSetup.abstractBannerImage, render: renderIcon },
                { label: 'Workshop Banner', value: homeSetup.workshopBannerImage, render: renderIcon },
                { label: 'Conference Banner', value: homeSetup.conferenceBannerImage, render: renderIcon },
            ]
        },
        {
            title: "Conference Payment Button Status",
            buttonText: "Payment Setup",
            path: "/admin/abstract/home-page",
            items: [
                { label: 'Group Registration', value: homeSetup.c_group_registration, render: renderIcon },
                { label: 'Online Payment', value: homeSetup.c_online_status, render: renderIcon },
                { label: 'Bank Payment', value: homeSetup.c_bank_status, render: renderIcon },
                { label: 'Cash Payment', value: homeSetup.c_cash_status, render: renderIcon },
                { label: 'Free Registration', value: homeSetup.c_free_status, render: renderIcon },
            ]
        },
         {
            title: "Workshop Payment Button Status",
            buttonText: "Payment Setup",
            path: "/admin/abstract/home-page",
            items: [
                { label: 'Online Payment', value: homeSetup.online_status, render: renderIcon },
                { label: 'Bank Payment', value: homeSetup.bank_status, render: renderIcon },
                { label: 'Cash Payment', value: homeSetup.cash_status, render: renderIcon },
                { label: 'Free Registration', value: homeSetup.free_status, render: renderIcon },
            ]
        },
        {
            title: "SMTP Configuration",
            buttonText: "SMTP Setup",
            path: "/admin/email/setting",
            items: [
                { label: 'SMTP Configured', value: isSMTPConfigured(), render: renderIcon },
            ]
        },
        {
            title: "Online Payment and Currency",
            buttonText: "Currency Setup",
            path: "/admin/payment/setting",
            items: [
                { label: 'Saudi Riyal (SAR)', value: settings.PAYTAB_MODE === "1", render: renderIcon },
                { label: 'UAE Dirham (AED)', value: settings.PAYTAB_MODE_ONE === "1", render: renderIcon },
                { label: 'USD Dollar (USD)', value: settings.PAYTAB_MODE_TWO === "1", render: renderIcon },
            ]
        },
        {
            title: "Email Template Images",
            buttonText: "Images Setup",
            path: "/admin/general/setting",
            items: [
                { label: 'Header Image', value: settings.COMPANY_HEADER, render: renderIcon },
                { label: 'Footer Image', value: settings.COMPANY_FOOTER, render: renderIcon },
            ]
        },
        {
            title: "Conference Badges",
            buttonText: "Badges Setup",
            path: "/admin/conference/badge",
            additionalButtons: [
                { text: "Exhibition", path: "/admin/options/assign" },
                { text: "Affiliation", path: "/admin/affiliation/badge" } ,
                { text: "Conference Certificate", path: "/admin/conference/conference-event-certificate" }
            ],
            items: [
                { label: 'Conference Badge', value: getOptionItems.conference_badge, render: renderIconForImage },
                { label: 'Affiliation Badge', value: getOptionItems.conference_affiliation_badge, render: renderIconForImage },
                { label: 'Exhibition Badge', value: getOptionItems.guest_badge, render: renderIconForImage },
                { label: 'Conference Certificate', value: getOptionItems.conference_certificate, render: renderIconForImage },
            ]
        },
        {
            title: "Conference Email Templates",
            buttonText: "Templates Setup",
            path: "/admin/conference/index-email",
            items: [
                { label: 'Registration', value: hasTemplate(15), render: renderIcon },
                { label: 'Bank Verification', value: hasTemplate(17), render: renderIcon },
                { label: 'Participants Reminder', value: hasTemplate(18), render: renderIcon },
                { label: 'Conference Coupon', value: hasTemplate(19), render: renderIcon },
                { label: 'Free Registration', value: hasTemplate(20), render: renderIcon },
                { label: 'QR Code Reminder', value: hasTemplate(21), render: renderIcon },
                { label: 'Free Reg Without Verify', value: hasTemplate(22), render: renderIcon },
                { label: 'Online Payment', value: hasTemplate(24), render: renderIcon },
                { label: 'Affiliation Bank Verify', value: hasTemplate(25), render: renderIcon },
                { label: 'Affiliation Free Reg', value: hasTemplate(26), render: renderIcon },
                { label: 'Claim Coupon', value: hasTemplate(27), render: renderIcon },
            ]
        },
        {
            title: "Abstract Email Templates",
            buttonText: "Abstract Templates Setup",
            path: "/admin/abstract/index-template",
            items: [
                { label: 'Abstract Registration', value: hasAbstractTemplate(1), render: renderIcon },
                { label: 'Abstract Issue', value: hasAbstractTemplate(2), render: renderIcon },
                { label: 'Abstract Reject', value: hasAbstractTemplate(3), render: renderIcon },
                { label: 'Oral Presentation', value: hasAbstractTemplate(4), render: renderIcon },
                { label: 'Digital Poster', value: hasAbstractTemplate(5), render: renderIcon },
                { label: 'Abstract General', value: hasAbstractTemplate(6), render: renderIcon }
            ]
        },
        {
            title: "Workshop Email Templates",
            buttonText: "Workshop Templates Setup",
            path: "/admin/workshop/index-email",
            items: [
                { label: 'Workshop Registration', value: hasWorkshopTemplate(10), render: renderIcon },
                { label: 'Workshop Free Regisrtation', value: hasWorkshopTemplate(11), render: renderIcon },
                { label: 'Payment Verification', value: hasWorkshopTemplate(12), render: renderIcon },
                { label: 'Workshop Participant Reminder', value: hasWorkshopTemplate(13), render: renderIcon },
                { label: 'Workshop Coupon', value: hasWorkshopTemplate(14), render: renderIcon },
                { label: 'Workshop Online Registration', value: hasWorkshopTemplate(23), render: renderIcon },

            ]
        },
        {
            title: "Exhibition Email Templates",
            buttonText: "Exhibition Templates Setup",
            path: "/admin/exhibition/index-email",
            items: [
                { label: 'Exhibition Registration', value: hasExhibitionTemplate(7), render: renderIcon },
                { label: 'Already Guest Register', value: hasExhibitionTemplate(8), render: renderIcon },
                { label: 'Auto Verification', value: hasExhibitionTemplate(9), render: renderIcon },

            ]
        },




    ];

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="50vh" // Centers in most of the viewport
                width="100%"
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{
            p: isMobile ? 1 : 3,
            pt: 8, // Add top padding to account for navbar
            marginTop: '54px' // Push content below fixed navbar
        }}>
            <Typography
                variant="h5"
                gutterBottom
                sx={{
                    mb: 3,
                    fontWeight: 'bold',
                    textAlign: 'center', // Center the text
                    width: '100%', // Ensure full width for proper centering
                }}
            >
                Required Setup for Event
            </Typography>
            <Button
                variant="contained"
                color="primary"
                onClick={() => history.goBack()}
                sx={{ float: 'right' , backgroundColor:abstractHomeSetup?.color_code_two || gradientBackground, }}
            >
                Back to Dashboard
            </Button>

            <Grid container spacing={3}>
                {sections.map((section, index) => (
                    <Grid item xs={12} sm={6} md={6} lg={4} key={index}>
                        <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <CardHeader
                                title={
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        ✅ {section.title}
                                    </Typography>
                                }
                                sx={{ pb: 0 }}
                            />
                            <CardContent sx={{ flexGrow: 1, p: 2 }}>
                                <Grid container spacing={1}>
                                    {section.items.map((item, itemIndex) => (
                                        <React.Fragment key={itemIndex}>
                                            <Grid item xs={8}>
                                                <Typography variant="body2">{item.label}</Typography>
                                            </Grid>
                                            <Grid item xs={4} sx={{ textAlign: 'right' }}>
                                                {item.render(item.value)}
                                            </Grid>
                                        </React.Fragment>
                                    ))}
                                </Grid>
                            </CardContent>
                            <Divider />
                            <CardActions sx={{ p: 1, justifyContent: 'flex-end' }}>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        color="primary"
                                        sx={{ textTransform: 'none' }}
                                        onClick={() => history.push(section.path)}
                                    >
                                        {section.buttonText}
                                    </Button>
                                    {section.additionalButtons?.map((button, idx) => (
                                        <Button
                                            key={idx}
                                            variant="outlined"
                                            size="small"
                                            color="secondary"
                                            sx={{ textTransform: 'none' }}
                                            onClick={() => history.push(button.path)}
                                        >
                                            {button.text} 
                                        </Button>
                                    ))}
                                </Box>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default RequiredForEvent;